/**
 * Sprint 1070.C3 — Web Studio shell config (shared by WebStudioPage and
 * WebsiteBuilderPage for chrome continuity).
 *
 * Exports:
 *   - WEB_STUDIO_IDENTITY  : { id, label, tagline, missionLabel, accent }
 *   - buildWebStudioSidebar()       — returns the SECTION_REGISTRY entries as
 *     SidebarGroup-shaped array (computed at runtime because the lucide icon
 *     refs need to be live JSX values, not strings).
 *   - SECTION_TO_STAGE     : { [sectionKey]: stageSlug } map.
 *   - buildMissionStages(state, t) — returns the 3 mission stages with
 *     freshly-counted live numbers from the studio's state.
 *
 * This is the SINGLE source of truth for Web Studio chrome. Any page that
 * wants to feel "inside Web Studio" (mission breadcrumb + sidebar + toolbar)
 * imports from here. Currently consumed by WebStudioPage + WebsiteBuilderPage
 * (when `?source=web-studio` is set).
 */
import {
  Globe, Sparkles, Bot, LayoutGrid, Wrench, FileText, Plug, Package,
  Mail, ShoppingCart, Rocket, Activity, Upload,
} from "lucide-react";

export const WEB_STUDIO_IDENTITY = {
  id: "web",
  label: "Web Studio",
  tagline: "Create · Maintain · Integrate · Distribute",
  missionLabel: "Build. Ship. Optimize.",
  accent: "#38BDF8",
  icon: Globe,
};

export const SECTION_TO_STAGE = {
  create: "build",
  go_live: "ship",
  domains: "ship",
  maintain: "optimize",
  integrate: "optimize",
  widgets: "optimize",
};

export function buildWebStudioSidebar() {
  return [
    {
      key: "create",
      label: "Create",
      icon: Sparkles,
      items: [
        { key: "create-templates", label: "From Template",    icon: LayoutGrid, href: "/marketing/website?focus=templates&source=web-studio" },
        { key: "create-cobuild",   label: "Co-build with AI", icon: Bot,        href: "/web-studio?section=create&mode=cobuild" },
        { key: "create-import",    label: "Import a Site",    icon: Upload,     href: "/web-studio?section=create&mode=import" },
      ],
    },
    {
      key: "go_live",
      label: "Go Live",
      icon: Rocket,
      items: [
        { key: "go-live-publish", label: "Publish",        icon: Rocket, href: "/web-studio?section=go_live" },
        { key: "go-live-domains", label: "Custom Domains", icon: Globe,  href: "/web-studio?section=domains" },
      ],
    },
    {
      key: "maintain",
      label: "Maintain",
      icon: Wrench,
      items: [
        { key: "maintain-published", label: "Published Sites", icon: Globe,    href: "/marketing/website?tab=projects&filter=published&source=web-studio" },
        { key: "maintain-drafts",    label: "Draft Sites",     icon: FileText, href: "/marketing/website?tab=projects&filter=draft&source=web-studio" },
        { key: "maintain-blog",      label: "Blog Studio",     icon: FileText, href: "/web-studio?section=maintain&mode=blog" },
      ],
    },
    {
      key: "integrate",
      label: "Integrate",
      icon: Plug,
      items: [
        { key: "integrate-hub",     label: "External Site Hub", icon: Globe,    href: "/platform/external-site?source=web-studio" },
        { key: "integrate-connect", label: "Connect Existing",  icon: Plug,     href: "/platform/external-site/integrate?source=web-studio" },
        { key: "integrate-funnel",  label: "Conversion Funnel", icon: Activity, href: "/platform/external-site/funnel?source=web-studio" },
      ],
    },
    {
      key: "widgets",
      label: "Widgets",
      icon: Package,
      items: [
        { key: "widgets-catalog",    label: "Catalog Widgets",    icon: ShoppingCart, href: "/platform/external-site/catalog-widgets?source=web-studio" },
        { key: "widgets-newsletter", label: "Newsletter Widgets", icon: Mail,         href: "/platform/external-site/newsletter-widgets?source=web-studio" },
        { key: "widgets-shop",       label: "Shop Widgets",       icon: ShoppingCart, href: "/platform/external-site/shop-widgets?source=web-studio" },
      ],
    },
  ];
}

export function buildMissionStages(state, t) {
  if (!state) return [];
  const activeDrafts = Math.max((state.native_count || 0) - (state.published_count || 0), 0);
  const published = state.published_count || 0;
  const external = state.external_count || 0;
  return [
    { slug: "build",    label: t ? t("Build")    : "Build",    count: activeDrafts, href: "/web-studio?section=create" },
    { slug: "ship",     label: t ? t("Ship")     : "Ship",     count: published,    href: "/web-studio?section=go_live" },
    { slug: "optimize", label: t ? t("Optimize") : "Optimize", count: external,     href: "/web-studio?section=maintain" },
  ];
}
